/**
 * Scadenziario degli adempimenti formativi dei clienti.
 *
 * Sostituisce il foglio Excel a matrice che i clienti tengono oggi: una riga
 * per dipendente e due colonne per corso. Lì aggiungere un corso vuol dire
 * aggiungere colonne, chi esce dall'azienda viene cancellato portandosi via
 * lo storico, e per sapere cosa scade bisogna leggersi la griglia.
 *
 * Qui si parte dalla domanda vera — cosa scade, e chi è scoperto — e il resto
 * (personale, corsi, certificati) sta dietro.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { clientiApi, formazioneApi } from '../api';
import Modal from '../components/Modal';
import ImportaScadenziario from '../components/ImportaScadenziario';
import CruscottoFormazione from '../components/CruscottoFormazione';
import ComunicaScadenze from '../components/ComunicaScadenze';
import NostriCorsi from '../components/NostriCorsi';

const oggi = () => new Date().toISOString().slice(0, 10);
const fmtData = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';
const personaVuota = () => ({ nome: '', qualifica: '', reparto: '', sesso: '', tipo_contratto: '',
                              data_assunzione: '', data_cessazione: '', note: '' });

/** Quanto manca, detto come lo si direbbe a voce. */
function quandoScade(giorni) {
  const g = Number(giorni);
  if (g < 0) return { testo: `scaduto da ${-g} ${-g === 1 ? 'giorno' : 'giorni'}`, classe: 'text-red-700 bg-red-50' };
  if (g === 0) return { testo: 'scade oggi', classe: 'text-red-700 bg-red-50' };
  if (g <= 30) return { testo: `fra ${g} ${g === 1 ? 'giorno' : 'giorni'}`, classe: 'text-amber-800 bg-amber-50' };
  if (g <= 90) return { testo: `fra ${Math.round(g / 30)} mesi`, classe: 'text-yellow-800 bg-yellow-50' };
  return { testo: `fra ${Math.round(g / 30)} mesi`, classe: 'text-gray-600 bg-gray-50' };
}

export default function Formazione() {
  const [clienti, setClienti] = useState([]);
  const [riepilogo, setRiepilogo] = useState([]);
  const [clienteId, setClienteId] = useState('');
  const [dati, setDati] = useState(null);           // corsi, personale, attestati
  const [scad, setScad] = useState(null);
  const [vista, setVista] = useState('scadenziario');
  const [caricando, setCaricando] = useState(false);
  const [modal, setModal] = useState(null);         // persona in modifica
  const [form, setForm] = useState(personaVuota());
  const [attestatoDi, setAttestatoDi] = useState(null);
  const [importa, setImporta] = useState(false);
  // Il nostro listino non dipende dal cliente: si apre da qui, perché è qui
  // che ci si accorge di un corso da proporre.
  const [listino, setListino] = useState(false);
  const [comunica, setComunica] = useState(false);
  const [mostraCessati, setMostraCessati] = useState(false);
  // Ricerca propria dell'elenco personale: quella dello scadenziario filtra
  // le scadenze, qui si cerca una persona — anche chi non ha scadenze.
  const [cercaPersona, setCercaPersona] = useState('');
  // I filtri del cruscotto: si accendono cliccando un grafico e si spengono
  // ricliccando. Stanno qui perché la tabella sotto deve vederli.
  const [filtri, setFiltri] = useState({ mese: null, corso: null, persona: null, finestra: null });
  const [cerca, setCerca] = useState('');
  const tabella = useRef(null);
  // Un filtro scelto sul cruscotto cambia una tabella che sta sotto, fuori
  // schermo: senza portarci l'occhio sembra che il clic non abbia fatto
  // niente. Si scorre, e si vede il risultato.
  const vaiAllaTabella = () => setTimeout(
    () => tabella.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  const setFiltro = (k, v) => { setFiltri(f => ({ ...f, [k]: v })); if (v) vaiAllaTabella(); };
  const setPiuFiltri = (obj) => { setFiltri(f => ({ ...f, ...obj })); vaiAllaTabella(); };
  const azzera = () => { setFiltri({ mese: null, corso: null, persona: null, finestra: null }); setCerca(''); };
  const attivi = Object.values(filtri).filter(Boolean).length + (cerca.trim() ? 1 : 0);

  useEffect(() => { clientiApi.list().then(setClienti).catch(() => {}); }, []);
  useEffect(() => { formazioneApi.riepilogo().then(setRiepilogo).catch(() => {}); }, []);

  const carica = async (id = clienteId) => {
    if (!id) { setDati(null); setScad(null); return; }
    setCaricando(true);
    try {
      const [d, s] = await Promise.all([formazioneApi.cliente(id), formazioneApi.scadenziario(id)]);
      setDati(d); setScad(s);
    } catch (e) { alert(e?.error || e); } finally { setCaricando(false); }
  };
  useEffect(() => { carica(); }, [clienteId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const corsi = dati?.corsi || [];
  const nomeCorso = useMemo(
    () => Object.fromEntries(corsi.map(c => [c.id, c.nome])), [corsi]);
  const personale = useMemo(() => {
    const parole = cercaPersona.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return (dati?.personale || [])
      .filter(p => mostraCessati || !p.data_cessazione)
      .filter(p => {
        if (!parole.length) return true;
        const t = [p.nome, p.qualifica, p.reparto, p.tipo_contratto, p.note]
          .filter(Boolean).join(' ').toLowerCase();
        return parole.every(w => t.includes(w));
      });
  }, [dati, mostraCessati, cercaPersona]);
  const attestatiDi = (id) => (dati?.attestati || []).filter(a => a.personale_id === id);

  // Scaduti, imminenti e tranquilli sono la stessa cosa guardata da finestre
  // diverse: per filtrare servono insieme, in una lista sola.
  const tutteLeScadenze = useMemo(() => [
    ...(scad?.scaduti || []), ...(scad?.entro30 || []),
    ...(scad?.entro90 || []), ...(scad?.oltre || []),
  ], [scad]);

  const visibili = useMemo(() => {
    const parole = cerca.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return tutteLeScadenze.filter(r => {
      if (filtri.corso && r.corso !== filtri.corso) return false;
      if (filtri.persona && r.persona !== filtri.persona) return false;
      if (filtri.mese === 'scaduti' && r.giorni >= 0) return false;
      if (filtri.mese && filtri.mese !== 'scaduti'
          && String(r.scadenza).slice(0, 7) !== filtri.mese) return false;
      if (filtri.finestra === 'scaduti' && r.giorni >= 0) return false;
      if (filtri.finestra === '30' && !(r.giorni >= 0 && r.giorni <= 30)) return false;
      if (filtri.finestra === '90' && !(r.giorni >= 0 && r.giorni <= 90)) return false;
      if (parole.length) {
        const t = `${r.persona} ${r.corso} ${r.qualifica || ''}`.toLowerCase();
        if (!parole.every(w => t.includes(w))) return false;
      }
      return true;
    }).sort((a, b) => a.giorni - b.giorni);
  }, [tutteLeScadenze, filtri, cerca]);

  const salvaPersona = async () => {
    try {
      if (modal.id) await formazioneApi.aggiornaPersona(modal.id, form);
      else await formazioneApi.creaPersona(clienteId, form);
      setModal(null); carica();
    } catch (e) { alert(e?.error || e); }
  };

  const eliminaPersona = async (p) => {
    if (!confirm(`Eliminare ${p.nome}?`)) return;
    try { await formazioneApi.eliminaPersona(p.id); carica(); }
    catch (e) { alert(e?.error || e); }
  };

  const cliente = clienti.find(c => String(c.id) === String(clienteId));

  // Il nome di una persona è sempre un punto d'ingresso alla sua scheda:
  // leggendo una scadenza la domanda successiva è "e lei chi è, cos'altro ha
  // fatto?", e la risposta deve stare a un clic.
  const apriScheda = (id, nomePersona) => {
    const p = (dati?.personale || []).find(x =>
      String(x.id) === String(id) || x.nome === nomePersona);
    if (p) setAttestatoDi(p);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="page-title">Scadenziario formativo</h1>
        {clienteId && (
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={() => setComunica(true)}
                    title="Prepara la comunicazione delle scadenze, con la proposta dei nostri corsi">
              ✉ Comunica al cliente
            </button>
            <button className="btn-secondary" onClick={() => setImporta(true)}>
              ⬆ Importa da Excel
            </button>
            <button className="btn-primary"
                    onClick={() => { setForm(personaVuota()); setModal({ mode: 'new' }); }}>
              + Persona
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <select className="input max-w-sm" value={clienteId}
                onChange={e => { setClienteId(e.target.value); if (e.target.value) setListino(false); }}>
          <option value="">— Scegli il cliente —</option>
          {clienti.map(c => (
            <option key={c.id} value={c.id}>{c.ragione_sociale || c.nome}</option>
          ))}
        </select>
        {caricando && <span className="text-xs text-gray-400">Carico…</span>}
        <button type="button" onClick={() => setListino(v => !v)}
                title="Il catalogo dei corsi che eroghiamo e le date previste"
                className={`btn-sm btn ml-auto ${listino ? 'bg-brand-600 text-white'
                  : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}>
          🎓 I nostri corsi
        </button>
      </div>

      {listino && <NostriCorsi />}

      {/* Senza un cliente scelto si guarda il quadro d'insieme: è da lì che si
          capisce dove intervenire prima. */}
      {!clienteId && !listino && (
        <div className="card p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr className="text-xs text-gray-500 text-left">
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">In servizio</th>
                <th className="px-4 py-3">Scaduti</th>
                <th className="px-4 py-3">Entro 90 gg</th>
                <th className="px-4 py-3">Prossima scadenza</th>
              </tr>
            </thead>
            <tbody>
              {riepilogo.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                  Nessun cliente ha ancora uno scadenziario. Scegline uno qui sopra e importa il
                  suo Excel.
                </td></tr>
              )}
              {riepilogo.map(r => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setClienteId(String(r.id))}>
                  <td className="px-4 py-3 font-medium">{r.ragione_sociale || r.nome}</td>
                  <td className="px-4 py-3 text-gray-600">{r.in_servizio}</td>
                  <td className="px-4 py-3">
                    {r.scaduti > 0
                      ? <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 font-medium">{r.scaduti}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {r.in_scadenza > 0
                      ? <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">{r.in_scadenza}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{fmtData(r.prossima)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {clienteId && dati && !listino && (
        <>
          {/* I numeri che contano, prima di tutto il resto */}
          {/* Le schede sono il primo filtro: si clicca "Scaduti" e sotto
              restano solo quelli. Ricliccando si torna a vedere tutto. */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ['Scaduti', scad?.scaduti.length || 0, 'text-red-700', 'scaduti'],
              ['Entro 30 giorni', scad?.entro30.length || 0, 'text-amber-800', '30'],
              ['Entro 90 giorni', (scad?.entro30.length || 0) + (scad?.entro90.length || 0), 'text-yellow-700', '90'],
              ['Scoperti', scad?.mancanti.length || 0, 'text-gray-700', null],
            ].map(([t, n, cl, f]) => (
              <button key={t} type="button" disabled={!f}
                      onClick={() => f && setFiltro('finestra', filtri.finestra === f ? null : f)}
                      className={`card text-left transition ${f ? 'hover:border-brand-300 cursor-pointer' : 'cursor-default'}
                        ${filtri.finestra === f ? 'ring-2 ring-brand-400' : ''}`}>
                <div className="text-xs text-gray-500 uppercase tracking-wide">{t}</div>
                <div className={`text-2xl font-semibold ${n > 0 ? cl : 'text-gray-300'}`}>{n}</div>
              </button>
            ))}
          </div>

          <div className="flex gap-2 flex-wrap">
            {[['scadenziario', 'Scadenziario'],
              ['personale', `Personale (${(dati?.personale || []).filter(p => mostraCessati || !p.data_cessazione).length})`],
              ['corsi', `Corsi previsti (${corsi.length})`]].map(([v, l]) => (
              <button key={v} onClick={() => setVista(v)}
                      className={`btn-sm btn ${vista === v ? 'bg-brand-600 text-white'
                        : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}>{l}</button>
            ))}
          </div>

          {vista === 'scadenziario' && (
            <div className="space-y-4">
              <CruscottoFormazione righe={tutteLeScadenze} filtri={filtri}
                                  onFiltro={setFiltro} onFiltri={setPiuFiltri}
                                  onPersona={(nome) => apriScheda(null, nome)} />

              {/* Cosa si sta guardando, detto a parole e smontabile pezzo per
                  pezzo: con quattro filtri possibili, "0 risultati" senza
                  spiegazione è il modo più rapido per perdere fiducia. */}
              <div ref={tabella} className="scroll-mt-4">
              {attivi > 0 && (
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="text-xs text-gray-500">Stai guardando:</span>
                  {[
                    filtri.finestra && ['finestra', { scaduti: 'solo scaduti', 30: 'entro 30 giorni', 90: 'entro 90 giorni' }[filtri.finestra]],
                    filtri.corso && ['corso', filtri.corso],
                    filtri.persona && ['persona', filtri.persona],
                    filtri.mese && ['mese', filtri.mese === 'scaduti' ? 'già scaduti' : filtri.mese],
                    cerca.trim() && ['cerca', `“${cerca.trim()}”`],
                  ].filter(Boolean).map(([k, testo]) => (
                    <button key={k} type="button"
                            className="text-xs px-2 py-1 rounded-full bg-brand-50 border border-brand-200 text-brand-700 hover:bg-brand-100"
                            onClick={() => k === 'cerca' ? setCerca('') : setFiltri(f => ({ ...f, [k]: null }))}>
                      {testo} <span className="text-brand-400 ml-0.5">✕</span>
                    </button>
                  ))}
                  <button className="text-xs text-gray-500 hover:underline" onClick={azzera}>azzera tutto</button>
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <input className="input max-w-xs py-1 text-sm" placeholder="Cerca persona o corso…"
                       value={cerca} onChange={e => setCerca(e.target.value)} />
                <select className="input max-w-xs py-1 text-sm" value={filtri.corso || ''}
                        onChange={e => setFiltro('corso', e.target.value || null)}>
                  <option value="">Tutti i corsi</option>
                  {corsi.map(c => <option key={c.id} value={c.nome}>{c.nome}</option>)}
                </select>
                <select className="input w-44 py-1 text-sm" value={filtri.finestra || ''}
                        onChange={e => setFiltro('finestra', e.target.value || null)}>
                  <option value="">Tutte le scadenze</option>
                  <option value="scaduti">Solo scaduti</option>
                  <option value="30">Entro 30 giorni</option>
                  <option value="90">Entro 90 giorni</option>
                </select>
                {attivi > 0 && (
                  <button className="btn-secondary btn-sm" onClick={azzera}>
                    Azzera filtri ({attivi})
                  </button>
                )}
                <span className="text-xs text-gray-500 ml-auto">
                  {visibili.length} di {tutteLeScadenze.length}
                </span>
              </div>

              <div className="card p-0 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr className="text-xs text-gray-500 text-left">
                      <th className="px-4 py-2">Persona</th>
                      <th className="px-4 py-2">Qualifica</th>
                      <th className="px-4 py-2">Corso</th>
                      <th className="px-4 py-2">Fatto il</th>
                      <th className="px-4 py-2">Scade il</th>
                      <th className="px-4 py-2 text-right">Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibili.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                        Nessuna scadenza con questi filtri.
                      </td></tr>
                    )}
                    {visibili.map(r => {
                      const q = quandoScade(r.giorni);
                      return (
                        <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                          <td className="px-4 py-2">
                            <button className="font-medium text-brand-700 hover:underline"
                                    onClick={() => apriScheda(r.personale_id, r.persona)}>
                              {r.persona}
                            </button>
                          </td>
                          <td className="px-4 py-2 text-gray-500">{r.qualifica || '—'}</td>
                          <td className="px-4 py-2 text-gray-600">
                            {r.corso}
                            {r.sanitario && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-700">sanitario</span>}
                          </td>
                          <td className="px-4 py-2 text-gray-500">{fmtData(r.data)}</td>
                          <td className="px-4 py-2 text-gray-600">{fmtData(r.scadenza)}</td>
                          <td className="px-4 py-2 text-right">
                            <span className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${q.classe}`}>{q.testo}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              </div>

              {/* Chi non ha mai fatto un corso che gli spetta: non si vede
                  guardando le scadenze, perché non c'è niente da guardare. */}
              {scad?.mancanti.length > 0 && (
                <div className="card p-0 overflow-hidden">
                  <div className="px-4 py-2 bg-gray-50 border-b text-xs font-semibold uppercase tracking-wide text-gray-600">
                    Corsi obbligatori mai fatti · {scad.mancanti.length}
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {scad.mancanti.map((r, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-4 py-2">
                            <button className="font-medium text-brand-700 hover:underline"
                                    onClick={() => apriScheda(r.personale_id, r.persona)}>
                              {r.persona}
                            </button>
                          </td>
                          <td className="px-4 py-2 text-gray-600">{r.qualifica || '—'}</td>
                          <td className="px-4 py-2 text-gray-600">{r.corso}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {vista === 'personale' && (
            <div className="space-y-2">
              <div className="flex items-center gap-3 flex-wrap">
                <input className="input max-w-xs py-1 text-sm"
                       placeholder="Cerca per nome, qualifica, reparto…"
                       value={cercaPersona} onChange={e => setCercaPersona(e.target.value)} />
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input type="checkbox" className="w-4 h-4" checked={mostraCessati}
                         onChange={e => setMostraCessati(e.target.checked)} />
                  Mostra anche chi non è più in servizio
                </label>
                {cercaPersona && (
                  <button className="btn-secondary btn-sm" onClick={() => setCercaPersona('')}>
                    Azzera
                  </button>
                )}
                <span className="text-xs text-gray-500 ml-auto">
                  {personale.length} {personale.length === 1 ? 'persona' : 'persone'}
                </span>
              </div>
              <div className="card p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr className="text-xs text-gray-500 text-left">
                      <th className="px-4 py-3">Nominativo</th>
                      <th className="px-4 py-3">Qualifica</th>
                      <th className="px-4 py-3">Contratto</th>
                      <th className="px-4 py-3">Attestati</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {personale.length === 0 && (
                      <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                        Nessuna persona. Importa l'Excel del cliente o aggiungine una.
                      </td></tr>
                    )}
                    {personale.map(p => (
                      <tr key={p.id} className={`border-b last:border-0 hover:bg-gray-50 ${p.data_cessazione ? 'opacity-60' : ''}`}>
                        <td className="px-4 py-3 font-medium">
                          <button className="text-brand-700 hover:underline"
                                  onClick={() => setAttestatoDi(p)}>{p.nome}</button>
                          {p.data_cessazione && (
                            <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">
                              cessato il {fmtData(p.data_cessazione)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{p.qualifica || '—'}</td>
                        <td className="px-4 py-3 text-gray-500">{p.tipo_contratto || '—'}</td>
                        <td className="px-4 py-3">
                          <button className="text-brand-600 hover:underline"
                                  onClick={() => setAttestatoDi(p)}>
                            {attestatiDi(p.id).length} attestati
                          </button>
                        </td>
                        <td className="px-4 py-3 flex gap-1 justify-end">
                          <button className="btn-secondary btn-sm"
                                  onClick={() => { setForm({ ...personaVuota(), ...p,
                                    data_assunzione: p.data_assunzione?.slice(0, 10) || '',
                                    data_cessazione: p.data_cessazione?.slice(0, 10) || '' });
                                    setModal({ mode: 'edit', id: p.id }); }}>Modifica</button>
                          <button className="btn-danger btn-sm" onClick={() => eliminaPersona(p)}>Elimina</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {vista === 'corsi' && (
            <CorsiPrevisti clienteId={clienteId} corsi={corsi} onCambiato={carica} />
          )}
        </>
      )}

      {modal && (
        <Modal title={modal.mode === 'new' ? 'Nuova persona' : 'Modifica persona'}
               onClose={() => setModal(null)}>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="col-span-2">
              <label className="label">Nominativo *</label>
              <input className="input" value={form.nome}
                     onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} />
            </div>
            <div><label className="label">Qualifica</label>
              <input className="input" value={form.qualifica || ''}
                     onChange={e => setForm(p => ({ ...p, qualifica: e.target.value }))} /></div>
            <div><label className="label">Reparto</label>
              <input className="input" value={form.reparto || ''}
                     onChange={e => setForm(p => ({ ...p, reparto: e.target.value }))} /></div>
            <div><label className="label">Contratto</label>
              <input className="input" value={form.tipo_contratto || ''}
                     onChange={e => setForm(p => ({ ...p, tipo_contratto: e.target.value }))} /></div>
            <div>
              <label className="label">Sesso</label>
              <select className="input" value={form.sesso || ''}
                      onChange={e => setForm(p => ({ ...p, sesso: e.target.value }))}>
                <option value="">—</option><option value="M">M</option><option value="F">F</option>
              </select>
            </div>
            <div><label className="label">Assunzione</label>
              <input className="input" type="date" value={form.data_assunzione || ''}
                     onChange={e => setForm(p => ({ ...p, data_assunzione: e.target.value }))} /></div>
            <div>
              <label className="label">Cessazione</label>
              <input className="input" type="date" value={form.data_cessazione || ''}
                     onChange={e => setForm(p => ({ ...p, data_cessazione: e.target.value }))} />
              {/* Chi esce non si cancella: le sue scadenze tacciono, lo storico resta */}
              <p className="text-xs text-gray-400 mt-0.5">
                Indicandola, le sue scadenze smettono di comparire ma restano consultabili.
              </p>
            </div>
            <div className="col-span-2"><label className="label">Note</label>
              <input className="input" value={form.note || ''}
                     onChange={e => setForm(p => ({ ...p, note: e.target.value }))} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn-primary" onClick={salvaPersona}>Salva</button>
          </div>
        </Modal>
      )}

      {attestatoDi && (
        <AttestatiPersona
          persona={attestatoDi}
          corsi={corsi}
          attestati={attestatiDi(attestatoDi.id)}
          onClose={() => setAttestatoDi(null)}
          onCambiato={carica}
        />
      )}

      {comunica && (
        <ComunicaScadenze clienteId={clienteId}
                          nomeCliente={cliente?.ragione_sociale || cliente?.nome}
                          onClose={() => setComunica(false)} />
      )}

      {importa && (
        <ImportaScadenziario
          clienteId={clienteId}
          nomeCliente={cliente?.ragione_sociale || cliente?.nome}
          onClose={() => setImporta(false)}
          onFatto={() => { setImporta(false); carica(); formazioneApi.riepilogo().then(setRiepilogo).catch(() => {}); }}
        />
      )}
    </div>
  );
}

/** I corsi previsti per quel cliente, con ogni quanto vanno rifatti. */
function CorsiPrevisti({ clienteId, corsi, onCambiato }) {
  const [righe, setRighe] = useState(corsi);
  const [salvando, setSalvando] = useState(false);
  // Il nostro catalogo: serve ad abbinare "Antincendio" del cliente al corso
  // che eroghiamo noi, così la comunicazione delle scadenze porta con sé la
  // proposta giusta invece di un listino generico.
  const [catalogo, setCatalogo] = useState([]);
  useEffect(() => { setRighe(corsi); }, [corsi]);
  useEffect(() => { formazioneApi.catalogo().then(setCatalogo).catch(() => {}); }, []);

  const abbina = async (corso, catalogoId) => {
    try { await formazioneApi.abbinaCorso(corso.id, catalogoId || null); onCambiato(); }
    catch (e) { alert(e?.error || e); }
  };

  const set = (i, k, v) => setRighe(r => r.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const salva = async () => {
    setSalvando(true);
    try { await formazioneApi.salvaCorsi(clienteId, righe.filter(r => r.nome?.trim())); onCambiato(); }
    catch (e) { alert(e?.error || e); } finally { setSalvando(false); }
  };

  return (
    <div className="card space-y-3">
      <p className="text-xs text-gray-500">
        I corsi che quell'azienda deve avere, con ogni quanto vanno rifatti. La validità serve a
        calcolare la scadenza quando sul certificato non è scritta; lasciandola vuota il corso
        non scade. Spuntando <strong>obbligatorio</strong> il gestionale segnala chi non l'ha mai
        fatto: importando resta spento, perché il foglio elenca i corsi svolti, non quelli dovuti.
        Un corso già usato negli attestati non si può togliere: togligli l'obbligo se non serve più.
      </p>
      {/* L'abbinamento è il punto in cui il corso del cliente diventa una cosa
          che sappiamo vendere: senza, nella comunicazione resta una scadenza
          e basta. */}
      <p className="text-xs text-gray-500 bg-gray-50 border rounded px-3 py-2">
        <strong>Nostro corso abbinato</strong> è dove l'“Antincendio” del cliente diventa il nostro
        corso a catalogo: fatto l'abbinamento, nella comunicazione delle scadenze quel corso esce
        con il nostro prezzo e la prima data utile.{' '}
        {catalogo.length === 0
          ? <>Il catalogo però è vuoto: apri <strong>🎓 I nostri corsi</strong>, in alto, e inseriscili.</>
          : <>I corsi e le date si gestiscono in <strong>🎓 I nostri corsi</strong>, in alto.
             {' '}Abbinati finora: {righe.filter(r => r.catalogo_id).length} su {righe.length}.</>}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b">
              <th className="pb-2">Corso</th>
              <th className="pb-2 w-32">Validità (mesi)</th>
              <th className="pb-2 w-28">Obbligatorio</th>
              <th className="pb-2 w-28">Sanitario</th>
              <th className="pb-2 w-56">Nostro corso abbinato</th>
              <th className="pb-2 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {righe.map((c, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="py-1 pr-2">
                  <input className="input py-1 text-sm" value={c.nome || ''}
                         onChange={e => set(i, 'nome', e.target.value)} />
                </td>
                <td className="py-1 pr-2">
                  <input className="input py-1 text-sm text-right" type="number" min="0"
                         placeholder="non scade" value={c.mesi_validita ?? ''}
                         onChange={e => set(i, 'mesi_validita', e.target.value)} />
                </td>
                <td className="py-1 pr-2 text-center">
                  <input type="checkbox" className="w-4 h-4" checked={c.obbligatorio !== false}
                         onChange={e => set(i, 'obbligatorio', e.target.checked)} />
                </td>
                <td className="py-1 pr-2 text-center">
                  <input type="checkbox" className="w-4 h-4" checked={!!c.sanitario}
                         onChange={e => set(i, 'sanitario', e.target.checked)} />
                </td>
                <td className="py-1 pr-2">
                  {c.id ? (
                    <select className="input py-1 text-xs" value={c.catalogo_id || ''}
                            onChange={e => abbina(c, e.target.value)}>
                      <option value="">— nessuno —</option>
                      {catalogo.filter(x => x.attivo).map(x => (
                        <option key={x.id} value={x.id}>{x.nome}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-xs text-gray-400">salva prima il corso</span>
                  )}
                </td>
                <td className="py-1 text-right">
                  <button className="text-gray-300 hover:text-red-600"
                          onClick={() => setRighe(r => r.filter((_, j) => j !== i))}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-2">
        <button className="btn-secondary btn-sm"
                onClick={() => setRighe(r => [...r, { nome: '', mesi_validita: '', obbligatorio: true }])}>
          + Corso
        </button>
        <button className="btn-primary btn-sm ml-auto" disabled={salvando} onClick={salva}>
          {salvando ? 'Salvo…' : 'Salva i corsi'}
        </button>
      </div>
    </div>
  );
}

/** Gli attestati di una persona: elenco, aggiunta, certificato allegato. */
function AttestatiPersona({ persona, corsi, attestati, onClose, onCambiato }) {
  const [nuovo, setNuovo] = useState({ corso_id: '', data: oggi(), scadenza: '', ente: '' });
  const [file, setFile] = useState(null);
  const [lavorando, setLavorando] = useState(false);

  const aggiungi = async () => {
    if (!nuovo.corso_id || !nuovo.data) return alert('Scegli il corso e la data.');
    setLavorando(true);
    try {
      await formazioneApi.creaAttestato(persona.id, nuovo, file);
      setNuovo({ corso_id: '', data: oggi(), scadenza: '', ente: '' }); setFile(null);
      onCambiato();
    } catch (e) { alert(e?.error || e); } finally { setLavorando(false); }
  };

  const elimina = async (a) => {
    if (!confirm('Eliminare questo attestato?')) return;
    try { await formazioneApi.eliminaAttestato(a.id); onCambiato(); }
    catch (e) { alert(e?.error || e); }
  };

  const nome = Object.fromEntries(corsi.map(c => [c.id, c.nome]));
  const corso = corsi.find(c => String(c.id) === String(nuovo.corso_id));

  // Da quanto è in azienda: il dato si legge meglio come durata che come data
  const anzianita = (() => {
    if (!persona.data_assunzione) return null;
    const da = new Date(persona.data_assunzione);
    const a = persona.data_cessazione ? new Date(persona.data_cessazione) : new Date();
    const mesi = Math.max(0, (a.getFullYear() - da.getFullYear()) * 12 + (a.getMonth() - da.getMonth()));
    const anni = Math.floor(mesi / 12), resto = mesi % 12;
    const t = [anni && `${anni} ${anni === 1 ? 'anno' : 'anni'}`,
               resto && `${resto} ${resto === 1 ? 'mese' : 'mesi'}`].filter(Boolean).join(' e ');
    return t || 'meno di un mese';
  })();

  // Lo stato di ogni attestato, per vedere a colpo d'occhio cosa è scaduto
  const stato = (a) => {
    if (!a.scadenza) return { t: 'non scade', c: 'bg-gray-100 text-gray-600' };
    const g = Math.round((new Date(a.scadenza) - new Date()) / 86400000);
    if (g < 0) return { t: `scaduto da ${-g} gg`, c: 'bg-red-100 text-red-700' };
    if (g <= 30) return { t: `fra ${g} gg`, c: 'bg-amber-100 text-amber-800' };
    if (g <= 90) return { t: `fra ${Math.round(g / 30)} mesi`, c: 'bg-yellow-100 text-yellow-800' };
    return { t: 'in regola', c: 'bg-green-100 text-green-700' };
  };

  return (
    <Modal title={persona.nome} onClose={onClose} size="lg">
      <div className="space-y-4 text-sm">
        {/* Chi è e da quando: la domanda che si fa aprendo una scheda */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-gray-50 border rounded-lg p-3">
          {[
            ['Qualifica', persona.qualifica],
            ['Reparto', persona.reparto],
            ['Contratto', persona.tipo_contratto],
            ['In azienda da', anzianita
              ? `${anzianita}${persona.data_cessazione ? ' (cessato)' : ''}`
              : null],
          ].map(([t, v]) => (
            <div key={t}>
              <div className="text-[11px] text-gray-500 uppercase tracking-wide">{t}</div>
              <div className={v ? 'text-gray-800 font-medium' : 'text-gray-300'}>{v || '—'}</div>
            </div>
          ))}
        </div>
        {!persona.data_assunzione && (
          <p className="text-xs text-amber-700">
            La data di assunzione non è indicata: l'Excel non la riporta, si aggiunge da
            <strong> Personale → Modifica</strong>.
          </p>
        )}
        {attestati.length === 0 ? (
          <p className="text-gray-400 text-center py-4">Nessun attestato registrato.</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b">
                <th className="pb-2">Corso</th><th className="pb-2">Data</th>
                <th className="pb-2">Scadenza</th><th className="pb-2">Ente</th><th className="pb-2"></th>
              </tr>
            </thead>
            <tbody>
              {attestati.map(a => (
                <tr key={a.id} className="border-b last:border-0">
                  <td className="py-2">{nome[a.corso_id] || '—'}</td>
                  <td className="py-2 text-gray-600">{fmtData(a.data)}</td>
                  <td className="py-2 text-gray-600">
                    {fmtData(a.scadenza)}
                    <span className={`ml-2 text-[11px] px-1.5 py-0.5 rounded ${stato(a).c}`}>{stato(a).t}</span>
                  </td>
                  <td className="py-2 text-gray-500">{a.ente || '—'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {a.nome_file && (
                      <button className="text-brand-600 hover:underline mr-2"
                              onClick={() => formazioneApi.apriCertificato(a.id)}>certificato</button>
                    )}
                    <button className="text-gray-300 hover:text-red-600" onClick={() => elimina(a)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="border rounded-lg p-3 bg-gray-50 space-y-2">
          <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Nuovo attestato</div>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <label className="label text-xs">Corso</label>
              <select className="input py-1 text-sm" value={nuovo.corso_id}
                      onChange={e => setNuovo(p => ({ ...p, corso_id: e.target.value }))}>
                <option value="">— scegli —</option>
                {corsi.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
            <div><label className="label text-xs">Data del corso</label>
              <input className="input py-1 text-sm" type="date" value={nuovo.data}
                     onChange={e => setNuovo(p => ({ ...p, data: e.target.value }))} /></div>
            <div>
              <label className="label text-xs">Scadenza</label>
              <input className="input py-1 text-sm" type="date" value={nuovo.scadenza}
                     onChange={e => setNuovo(p => ({ ...p, scadenza: e.target.value }))} />
              {/* Si calcola da sé, ma sul certificato può esserci scritta un'altra */}
              <p className="text-[11px] text-gray-400 mt-0.5">
                {corso?.mesi_validita
                  ? `Vuota: calcolata a ${corso.mesi_validita} mesi dalla data.`
                  : 'Vuota: il corso non scade.'}
              </p>
            </div>
            <div><label className="label text-xs">Ente formatore</label>
              <input className="input py-1 text-sm" value={nuovo.ente}
                     onChange={e => setNuovo(p => ({ ...p, ente: e.target.value }))} /></div>
            <div>
              <label className="label text-xs">Certificato (PDF)</label>
              <input type="file" className="input py-1 text-xs" accept=".pdf,image/*"
                     onChange={e => setFile(e.target.files?.[0] || null)} />
            </div>
          </div>
          <button className="btn-primary btn-sm" disabled={lavorando} onClick={aggiungi}>
            {lavorando ? 'Salvo…' : 'Aggiungi'}
          </button>
        </div>
      </div>
      <div className="flex justify-end mt-4">
        <button className="btn-secondary" onClick={onClose}>Chiudi</button>
      </div>
    </Modal>
  );
}
